import { get, map } from "lodash";
import { CanvasRenderConfig, getCanvasRender } from "./canvas-render";
import { useImageStore } from "entity-store/image-store/use-image-store";
import { SaveImageConfig } from "entity-store/image-store/use-image-store-remote";
import { PosterProject, GPSSource } from "PosterEditor/model/poster-project";
import { MapStaticImageOptions } from "PosterEditor/model/poster-project";
import { mapboxLngLatBoundsFromCoordinateBounds } from "PosterEditor/hooks/use-coordinates";
import { useMapBoxUtils } from "PosterEditor/hooks/use-mapbox-utils";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { v4 as uuid } from "uuid";
import { useState } from "react";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";

export interface ProjectRenderConfig {
  project: PosterProject;
  map: React.MutableRefObject<any>;
  gpsSource: GPSSource | undefined;
}

export const useRenderProject = (authToken: string | undefined) => {
  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ?? "";
  const { project, setLastRenderID, markRendered, imageCache } =
    usePosterProject();
  const {
    screenSizeFromProject,
    fullRenderSizeFromProject,
    mapRenderSizeFromProject,
    borderWidthInPixels,
  } = useSizePresets();

  const { getStaticMapURL } = useMapBoxUtils(accessToken);
  const { saveImage } = useImageStore();
  const [isRendering, setIsRendering] = useState<boolean>(false);

  const renderProject = async (config: ProjectRenderConfig) => {
    setIsRendering(true);
    const container = config.map.current.getContainer();
    const rect = container.getBoundingClientRect();

    const renderSize = mapRenderSizeFromProject(project, true);
    const borderWidth = borderWidthInPixels(project);
    //TODO : fix this canvas cal witht th wrong confgig
    const canvasConfig: CanvasRenderConfig = {
      map: config.map,
      originalMapSize: { width: rect.width, height: rect.height },
      screenSize: screenSizeFromProject(config.project) ?? {
        width: 0,
        height: 0,
      },
      mapRenderSize: renderSize,
      borderWidthInPixels: borderWidth ?? 0,
      project: config.project,
      gpsSource: config.gpsSource,
      imageCache: imageCache,
    };

    const blob = await getCanvasRender(canvasConfig);
    // const url = URL.createObjectURL(blob);
    // const a = document.createElement("a");
    // a.href = url;
    // a.download = "canvas.png";
    // document.body.appendChild(a);
    // a.click();
    // document.body.removeChild(a);
    // URL.revokeObjectURL(url);
    const id = uuid();
    const saveConfig: SaveImageConfig = {
      image: blob,
      existingFilename: project.lastRenderID,
      newFilename: id,
      isPublic: false,
      folder: `renders/${config.project.id}`,
      mimeTypeParam: blob.type,
    };
    setLastRenderID(id);

    await saveImage(saveConfig);

    const mapBoxURL = mapBoxStaticURLFromProject(config.project);

    if (!mapBoxURL) {
      setIsRendering(false);
      console.error("MapBox URL is undefined");
      return;
    }
    if (!authToken) {
      setIsRendering(false);
      console.error("Auth token is undefined");
      return;
    }
    const request: RenderFunctionRequest = {
      projectID: config.project.id,
      mapBoxURL: mapBoxURL,
      mapSize: mapRenderSizeFromProject(config.project, true),
      posterSize: fullRenderSizeFromProject(config.project, true),
      borderColor: config.project.borderStyle.borderColor ?? "white",
      canvasImageID: id,
      authToken: authToken,
    };

    await callRenderFunction(request);
    markRendered(true);
  };

  const mapBoxStaticURLFromProject = (project: PosterProject) => {
    if (!project.bounds) {
      return;
    }
    const bounds = mapboxLngLatBoundsFromCoordinateBounds(project.bounds);

    const mapURLParms: MapStaticImageOptions = {
      southwest: bounds.getSouthWest(),
      northeast: bounds.getNorthEast(),
      project,
      isThumbnail: false,
    };
    const staticMapUrl = getStaticMapURL(mapURLParms);
    return staticMapUrl;
  };

  interface RenderFunctionRequest {
    projectID: string;
    mapBoxURL: string;
    mapSize: { width: number; height: number };
    posterSize: { width: number; height: number };
    borderColor: string;
    canvasImageID: string;
    authToken: string;
  }

  const callRenderFunction = async (params: RenderFunctionRequest) => {
    const border = Math.floor(
      (params.posterSize.width - params.mapSize.width) / 2
    );

    const url = process.env.REACT_APP_RENDER_URL!;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${params.authToken}`,
        },
        body: JSON.stringify({
          projectId: params.projectID,
          mapUrl: params.mapBoxURL,
          width: params.posterSize.width,
          height: params.posterSize.height,
          mapWidth: params.mapSize.width,
          mapHeight: params.mapSize.height,
          margin: border,
          marginColor: params.borderColor,
          canvasImageID: params.canvasImageID,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Render function failed:", {
          status: response.status,
          statusText: response.statusText,
          errorData,
        });
        throw new Error(
          `Render function failed with status ${response.status}: ${response.statusText}`
        );
      }
    } catch (err) {
      console.error(
        "Network or unexpected error calling render function:",
        err
      );
      throw new Error(
        "Network or unexpected error calling render function: " + err
      );
    } finally {
      setIsRendering(false);
    }
  };

  return {
    renderProject,
    isRendering,
  };
};
